import { useState, useCallback } from "react";

// ── 2024/2025 SA Tax Brackets ──────────────────────────────────────────────
const BRACKETS = [
  { min: 0,         max: 237100,   rate: 0.18, base: 0 },
  { min: 237101,    max: 370500,   rate: 0.26, base: 42678 },
  { min: 370501,    max: 512800,   rate: 0.31, base: 77362 },
  { min: 512801,    max: 673000,   rate: 0.36, base: 121475 },
  { min: 673001,    max: 857900,   rate: 0.39, base: 179147 },
  { min: 857901,    max: 1817000,  rate: 0.41, base: 251258 },
  { min: 1817001,   max: Infinity, rate: 0.45, base: 644489 },
];

const REBATES   = { primary: 17235, secondary: 9444, tertiary: 3145 };
const UIF_RATE  = 0.01;
const UIF_MAX   = 177.12;
const MED_CREDIT = { member: 364, dependant1: 364, extra: 246 };

function calcTax(taxable) {
  for (const b of BRACKETS) {
    if (taxable <= b.max) {
      return b.base + (taxable - b.min) * b.rate;
    }
  }
  return 0;
}

function fmt(n) {
  return "R " + Math.round(n).toLocaleString("en-ZA");
}

function pct(part, total) {
  if (!total) return "0%";
  return (part / total * 100).toFixed(1) + "%";
}

const TABS = ["Individual", "Provisional"];

export default function TaxCalculator() {
  const [tab, setTab]           = useState("Individual");
  const [age, setAge]           = useState("under65");
  const [income, setIncome]     = useState("");
  const [medMembers, setMedMembers] = useState(1);
  const [medDeps, setMedDeps]   = useState(0);
  const [raContrib, setRaContrib] = useState("");
  const [travelAllow, setTravelAllow] = useState("");
  const [otherIncome, setOtherIncome] = useState("");
  const [result, setResult]     = useState(null);

  // Provisional
  const [p1Income, setP1Income] = useState("");
  const [p1Tax, setP1Tax]       = useState("");
  const [pResult, setPResult]   = useState(null);

  const calculate = useCallback(() => {
    const gross    = parseFloat(income) || 0;
    const other    = parseFloat(otherIncome) || 0;
    const travel   = parseFloat(travelAllow) || 0;
    const ra       = parseFloat(raContrib) || 0;

    // Travel: 80% taxable
    const taxableTravel = travel * 0.8;
    // RA deduction: min of 27.5% of income OR R350,000
    const raDeduction = Math.min(ra, Math.min((gross + other) * 0.275, 350000));

    const totalIncome = gross + other + taxableTravel;
    const taxableIncome = Math.max(0, totalIncome - raDeduction);

    let grossTax = calcTax(taxableIncome);

    // Rebates
    let rebate = REBATES.primary;
    if (age === "65to74")    rebate += REBATES.secondary;
    if (age === "75plus")    rebate += REBATES.secondary + REBATES.tertiary;

    // Medical tax credit
    const actualMedCredit = MED_CREDIT.member * medMembers + MED_CREDIT.extra * Math.max(0, medDeps);

    const netTax    = Math.max(0, grossTax - rebate - actualMedCredit * 12);
    const uif       = Math.min(gross * UIF_RATE / 12, UIF_MAX) * 12;
    const monthly   = netTax / 12;
    const effective = taxableIncome > 0 ? netTax / gross : 0;
    const takeHome  = gross - netTax - uif;

    setResult({
      grossIncome: totalIncome, taxableIncome, grossTax,
      rebate, medCredit: actualMedCredit * 12, raDeduction,
      netTax, uif, monthly, effective, takeHome,
    });
  }, [income, otherIncome, travelAllow, raContrib, age, medMembers, medDeps]);

  const calcProvisional = useCallback(() => {
    const annual = parseFloat(p1Income) || 0;
    const paid   = parseFloat(p1Tax)   || 0;
    const est    = calcTax(annual);
    const rebate = REBATES.primary;
    const net    = Math.max(0, est - rebate);
    const due    = Math.max(0, net - paid);
    const over   = Math.max(0, paid - net);
    setPResult({ annual, estimated: net, paid, due, over });
  }, [p1Income, p1Tax]);

  const inputClass = "w-full px-3 py-2.5 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 placeholder-luxury-muted";
  const labelClass = "text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block";

  return (
    <div className="min-h-screen bg-luxury-bg p-6 lg:p-8">

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-lg bg-luxury-elevated border border-luxury-gold/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider text-luxury-muted">2024 / 2025 Tax Year</span>
        </div>
        <h1 className="text-2xl font-bold text-luxury-text">Tax Calculator</h1>
        <p className="text-sm text-luxury-secondary mt-1">Estimate your South African income tax, UIF and take-home pay.</p>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="flex gap-2 mb-6">
        {TABS.map(t => (
          <button key={t} onClick={() => { setTab(t); setResult(null); setPResult(null); }}
            className={`px-5 py-2 rounded-xl text-sm font-semibold border transition-all
              ${tab === t ? "bg-luxury-cta text-white border-transparent" : "bg-luxury-surface border-luxury-border text-luxury-secondary hover:bg-luxury-elevated"}`}>
            {t === "Individual" ? "🧾 Individual (ITR12)" : "📋 Provisional (IRP6)"}
          </button>
        ))}
      </div>

      {tab === "Individual" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Input Panel ────────────────────────────────────────── */}
          <div className="space-y-5">

            {/* Age */}
            <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
              <h3 className="font-semibold text-luxury-text text-sm mb-4">Personal Details</h3>
              <div>
                <label className={labelClass}>Age Group</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { val: "under65", label: "Under 65" },
                    { val: "65to74",  label: "65 – 74" },
                    { val: "75plus",  label: "75+" },
                  ].map(a => (
                    <button key={a.val} onClick={() => setAge(a.val)}
                      className={`py-2 rounded-xl text-xs font-semibold border transition-all
                        ${age === a.val ? "bg-luxury-cta text-white border-transparent" : "bg-luxury-elevated border-luxury-border text-luxury-secondary hover:text-luxury-text"}`}>
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Income */}
            <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
              <h3 className="font-semibold text-luxury-text text-sm mb-4">Annual Income</h3>
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Gross Employment Income (R)</label>
                  <input value={income} onChange={e => setIncome(e.target.value)} type="number" placeholder="e.g. 450000" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Travel Allowance (R) — 80% is taxable</label>
                  <input value={travelAllow} onChange={e => setTravelAllow(e.target.value)} type="number" placeholder="e.g. 60000" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Other Income — rental, interest, etc. (R)</label>
                  <input value={otherIncome} onChange={e => setOtherIncome(e.target.value)} type="number" placeholder="e.g. 24000" className={inputClass} />
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
              <h3 className="font-semibold text-luxury-text text-sm mb-4">Deductions & Credits</h3>
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Retirement Annuity Contributions (R)</label>
                  <input value={raContrib} onChange={e => setRaContrib(e.target.value)} type="number" placeholder="Max 27.5% of income / R350,000" className={inputClass} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Medical Aid Members</label>
                    <select value={medMembers} onChange={e => setMedMembers(Number(e.target.value))} className={inputClass}>
                      {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Additional Dependants</label>
                    <select value={medDeps} onChange={e => setMedDeps(Number(e.target.value))} className={inputClass}>
                      {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={calculate}
              disabled={!income}
              className="w-full py-3 rounded-xl bg-luxury-cta hover:bg-luxury-cta-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm transition-colors active:scale-[0.99]"
            >
              Calculate Tax Estimate
            </button>
          </div>

          {/* ── Result Panel ───────────────────────────────────────── */}
          <div>
            {!result ? (
              <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-12 text-center h-full flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-2xl bg-luxury-elevated flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-luxury-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="font-semibold text-luxury-text mb-1">Enter your income details</p>
                <p className="text-sm text-luxury-secondary">Fill in the form and click Calculate to see your tax estimate.</p>
              </div>
            ) : (
              <div className="space-y-4">

                {/* Key figures */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Annual Tax",    value: fmt(result.netTax),    color: "text-red-600 dark:text-red-400",       accent: "border-t-red-400" },
                    { label: "Monthly Tax",   value: fmt(result.monthly),   color: "text-amber-600 dark:text-amber-400",   accent: "border-t-amber-400" },
                    { label: "Annual UIF",    value: fmt(result.uif),       color: "text-purple-600 dark:text-purple-400", accent: "border-t-purple-400" },
                    { label: "Take-Home Pay", value: fmt(result.takeHome),  color: "text-luxury-cta",                      accent: "border-t-luxury-cta" },
                  ].map(s => (
                    <div key={s.label} className={`rounded-2xl p-4 bg-luxury-surface border border-luxury-border border-t-2 ${s.accent}`}>
                      <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                      <div className="text-xs text-luxury-secondary mt-0.5 uppercase tracking-wide">{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Effective rate */}
                <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-luxury-text">Effective Tax Rate</span>
                    <span className="text-2xl font-bold text-luxury-gold">{pct(result.netTax, parseFloat(income)||1)}</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-luxury-elevated overflow-hidden">
                    <div className="h-full bg-luxury-cta rounded-full transition-all" style={{ width: pct(result.netTax, parseFloat(income)||1) }} />
                  </div>
                </div>

                {/* Breakdown */}
                <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-luxury-muted mb-3">Full Breakdown</h4>
                  <div className="space-y-2">
                    {[
                      { label: "Gross Income",        value: fmt(result.grossIncome),    color: "text-luxury-text" },
                      { label: "RA Deduction",         value: `− ${fmt(result.raDeduction)}`, color: "text-luxury-cta" },
                      { label: "Taxable Income",       value: fmt(result.taxableIncome),  color: "text-luxury-text" },
                      { label: "Gross Tax (brackets)", value: fmt(result.grossTax),       color: "text-red-600 dark:text-red-400" },
                      { label: "Primary Rebate",       value: `− ${fmt(result.rebate)}`,  color: "text-luxury-cta" },
                      { label: "Medical Tax Credit",   value: `− ${fmt(result.medCredit)}`,color:"text-luxury-cta" },
                      { label: "Net Tax Payable",      value: fmt(result.netTax),         color: "text-red-600 dark:text-red-400", bold: true },
                      { label: "UIF (annual)",         value: fmt(result.uif),            color: "text-luxury-secondary" },
                    ].map(row => (
                      <div key={row.label} className="flex justify-between items-center py-1.5 border-b border-luxury-border last:border-0">
                        <span className="text-xs text-luxury-secondary">{row.label}</span>
                        <span className={`text-xs ${row.bold ? "font-bold" : "font-semibold"} ${row.color}`}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-luxury-muted text-center px-2">
                  Estimate only — based on 2024/2025 SARS rates. Consult a tax practitioner for official advice.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "Provisional" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
              <h3 className="font-semibold text-luxury-text text-sm mb-4">Provisional Tax Estimate</h3>
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Estimated Annual Taxable Income (R)</label>
                  <input value={p1Income} onChange={e => setP1Income(e.target.value)} type="number" placeholder="e.g. 600000" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Tax Already Paid This Year (R)</label>
                  <input value={p1Tax} onChange={e => setP1Tax(e.target.value)} type="number" placeholder="e.g. 45000" className={inputClass} />
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-luxury-elevated border border-luxury-border p-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-luxury-muted mb-3">2025 Provisional Deadlines</h4>
              {[
                { date: "30 April 2025",   label: "1st Provisional Payment" },
                { date: "28 February 2026",label: "2nd Provisional Payment" },
                { date: "30 September 2026",label:"Optional 3rd Payment" },
              ].map(d => (
                <div key={d.date} className="flex justify-between items-center py-2 border-b border-luxury-border last:border-0">
                  <span className="text-xs text-luxury-secondary">{d.label}</span>
                  <span className="text-xs font-semibold text-luxury-text">{d.date}</span>
                </div>
              ))}
            </div>

            <button onClick={calcProvisional} disabled={!p1Income}
              className="w-full py-3 rounded-xl bg-luxury-cta hover:bg-luxury-cta-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm transition-colors">
              Calculate
            </button>
          </div>

          <div>
            {!pResult ? (
              <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-12 text-center h-full flex flex-col items-center justify-center">
                <p className="font-semibold text-luxury-text mb-1">Enter your estimates</p>
                <p className="text-sm text-luxury-secondary">Fill in the form to see your provisional tax position.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Estimated Annual Tax", value: fmt(pResult.estimated), color: "text-luxury-text",                   accent: "border-t-luxury-gold" },
                    { label: "Tax Paid So Far",       value: fmt(pResult.paid),      color: "text-luxury-cta",                   accent: "border-t-luxury-cta" },
                    { label: "Amount Due",            value: fmt(pResult.due),        color: "text-red-600 dark:text-red-400",   accent: "border-t-red-400" },
                    { label: "Overpaid",              value: fmt(pResult.over),       color: "text-emerald-600 dark:text-emerald-400", accent: "border-t-emerald-400" },
                  ].map(s => (
                    <div key={s.label} className={`rounded-2xl p-4 bg-luxury-surface border border-luxury-border border-t-2 ${s.accent}`}>
                      <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                      <div className="text-xs text-luxury-secondary mt-0.5 uppercase tracking-wide">{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className={`rounded-xl border p-4 ${pResult.due > 0 ? "border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/10" : "border-emerald-200 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-900/10"}`}>
                  <p className={`text-sm font-bold ${pResult.due > 0 ? "text-amber-700 dark:text-amber-400" : "text-emerald-700 dark:text-emerald-400"}`}>
                    {pResult.due > 0 ? `⚠️ You still owe ${fmt(pResult.due)} in provisional tax.` : `✅ You are ${fmt(pResult.over)} ahead on provisional tax payments.`}
                  </p>
                  <p className="text-xs text-luxury-secondary mt-1">
                    {pResult.due > 0 ? "Ensure payment is made by the 2nd provisional tax deadline to avoid penalties." : "No further provisional payments required unless your income increases significantly."}
                  </p>
                </div>
                <p className="text-xs text-luxury-muted text-center">Estimate only — based on 2024/2025 SARS rates.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
